import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

/** Patient fields the vitals table and details modal render. */
export interface VitalPatientSummary {
  id: string;
  firstName?: string;
  lastName?: string;
  registrationNumber?: string;
  age?: number;
  gender?: string;
  bloodGroup?: string;
  mobileNumber?: string;
  dateOfBirth?: string;
}

/** The HR employee who captured the reading. */
export interface VitalRecordedBy {
  id: string;
  name?: string;
  employeeCode?: string;
  designation?: { id?: string; name?: string } | null;
}

/** Visit context shown in the details modal. */
export interface VitalVisitSummary {
  id: string;
  visitNumber?: string;
  type?: string;
  status?: string;
  visitDate?: string;
}

export interface VitalSigns {
  id: string;
  visitId: string;
  patientId: string;
  /** Employee id. `null` when the recording account has no HR profile. */
  recordedById?: string | null;
  temperature?: number | null;
  temperatureUnit?: string | null;
  /** Stored as a `systolic/diastolic` string, e.g. `120/80`. */
  bloodPressure?: string | null;
  pulse?: number | null;
  spo2?: number | null;
  respiratoryRate?: number | null;
  weight?: number | null;
  weightUnit?: string | null;
  height?: number | null;
  heightUnit?: string | null;
  bmi?: number | null;
  painScore?: number | null;
  scheduleCode?: string | null;
  notes?: string | null;
  createdAt: string;
  updatedAt?: string;
  patient?: VitalPatientSummary | null;
  visit?: VitalVisitSummary | null;
  recordedBy?: VitalRecordedBy | null;
}

export interface CreateVitalSignsDto {
  visitId: string;
  patientId: string;
  /** Omitted by the dashboard: the API falls back to the signed-in employee. */
  recordedById?: string;
  temperature?: number;
  temperatureUnit?: string;
  bloodPressure?: string;
  pulse?: number;
  spo2?: number;
  respiratoryRate?: number;
  weight?: number;
  weightUnit?: string;
  height?: number;
  heightUnit?: string;
  painScore?: number;
  scheduleCode?: string;
  notes?: string;
}

/**
 * Update payload. A `null` value clears the corresponding column, which lets
 * the edit modal remove a mistyped reading; `undefined` leaves it untouched.
 */
export type UpdateVitalSignsDto = {
  [K in keyof Omit<CreateVitalSignsDto, 'visitId' | 'patientId'>]?:
    | CreateVitalSignsDto[K]
    | null;
};

type VitalsResponse = ApiResponse<VitalSigns[]> & { meta: PaginationMeta };

/**
 * Postgres returns `numeric` columns as strings. Normalise the vitals numbers
 * once here so the UI can treat them as real numbers everywhere.
 */
function normalizeVital(record: VitalSigns): VitalSigns {
  const toNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    ...record,
    temperature: toNumber(record.temperature),
    pulse: toNumber(record.pulse),
    spo2: toNumber(record.spo2),
    respiratoryRate: toNumber(record.respiratoryRate),
    weight: toNumber(record.weight),
    height: toNumber(record.height),
    bmi: toNumber(record.bmi),
    painScore: toNumber(record.painScore),
  };
}

function withNormalizedData(response: ApiResponse<VitalSigns[]>): VitalsResponse {
  return {
    ...response,
    data: Array.isArray(response.data) ? response.data.map(normalizeVital) : [],
  } as VitalsResponse;
}

export const vitalsService = {
  getVitals: (page = 1, limit = 20, search?: string, patientId?: string): Promise<VitalsResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (search) params.set('search', search);
    if (patientId) params.set('patientId', patientId);
    return get<VitalSigns[]>(`/vitals?${params.toString()}`).then(withNormalizedData);
  },

  getVitalsByPatient: (patientId: string, page = 1, limit = 50): Promise<VitalsResponse> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    return get<VitalSigns[]>(`/vitals/patient/${patientId}?${params.toString()}`).then(
      withNormalizedData,
    );
  },

  createVitals: (data: CreateVitalSignsDto): Promise<ApiResponse<VitalSigns>> => {
    return post<VitalSigns>('/vitals', data);
  },

  updateVitals: (id: string, data: UpdateVitalSignsDto): Promise<ApiResponse<VitalSigns>> => {
    return patch<VitalSigns>(`/vitals/${id}`, data);
  },

  deleteVitals: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/vitals/${id}`);
  },

  bulkDeleteVitals: (ids: string[]): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/vitals/bulk-delete', { ids });
  },
};
